import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { EmpresaTenant, GrupoEconomico, UsuarioWorkflow } from './models';
import workflowService from './workflowService';
import { descricaoVinculoGrupo, SEPARADOR_VINCULO } from './vinculoGrupo';
import CampoJustificativaSox from './CampoJustificativaSox';
import { useJustificativaSugerida } from './useJustificativaSugerida';
import { justificativaEdicaoGrupoEconomico, justificativaNovoGrupoEconomico } from './justificativaSox';
import { filtrarEmpresas } from './buscaEmpresas';
import { formatarCnpj } from '../../utils/String';

const CadastroGruposPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<{ fromOnboarding?: boolean; etapa?: number }>();
  const fromOnboarding = location.state?.fromOnboarding ?? false;
  const onboardingEtapa = location.state?.etapa ?? 1;

  const [grupos, setGrupos] = useState<GrupoEconomico[]>([]);
  const [empresas, setEmpresas] = useState<EmpresaTenant[]>([]);
  const [usuarios, setUsuarios] = useState<UsuarioWorkflow[]>([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<GrupoEconomico | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [buscaEmpresa, setBuscaEmpresa] = useState('');
  const [detalhe, setDetalhe] = useState<GrupoEconomico | null>(null);

  const [form, setForm] = useState({
    nome: '',
    status: 'ATIVO' as GrupoEconomico['status'],
    tenant_ids: [] as number[],
    tenantIdVincular: 0,
  });

  // Justificativa SOX já sugerida (nome do grupo + criação/edição); a pessoa revisa e edita se quiser.
  const justificativa = useJustificativaSugerida(
    editing ? justificativaEdicaoGrupoEconomico(form.nome) : justificativaNovoGrupoEconomico(form.nome));

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 5000);
  };

  const fetchDados = () => {
    setLoading(true);
    Promise.all([
      workflowService.listarGrupos(),
      workflowService.listarTenants(),
      workflowService.listarUsuarios(),
    ])
      .then(([g, e, u]) => {
        setGrupos(g);
        setEmpresas(e);
        setUsuarios(u);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchDados();
  }, []);

  const abrirNovo = () => {
    setEditing(null);
    setBuscaEmpresa('');
    setForm({ nome: '', status: 'ATIVO', tenant_ids: [], tenantIdVincular: 0 });
    justificativa.restaurar();
    setShowModal(true);
  };

  const abrirEditar = (g: GrupoEconomico) => {
    setEditing(g);
    setBuscaEmpresa('');
    setForm({ nome: g.nome, status: g.status, tenant_ids: g.tenant_ids, tenantIdVincular: 0 });
    justificativa.restaurar();
    setShowModal(true);
  };

  const empresasFiltradas = filtrarEmpresas(empresas, buscaEmpresa);

  const toggleEmpresa = (id: number) =>
    setForm(f => ({
      ...f,
      tenant_ids: f.tenant_ids.includes(id) ? f.tenant_ids.filter(x => x !== id) : [...f.tenant_ids, id],
    }));

  const salvar = () => {
    if (!form.nome.trim() || !justificativa.texto.trim()) return;
    setSaving(true);
    const dto = { nome: form.nome, status: form.status, justificativaSox: justificativa.texto.trim() || undefined };
    const novasEmpresas = editing
      ? form.tenant_ids.filter(id => !editing.tenant_ids.includes(id))
      : form.tenant_ids;

    const acao = editing
      ? workflowService.atualizarGrupo(editing.grupo_id, dto)
      : workflowService.criarGrupo(dto);

    acao
      .then(g => {
        const grupoId = g.grupo_id;
        const justSox = 'Vínculo registrado ao salvar grupo via interface SOX.';
        return Promise.all(
          novasEmpresas.map(tid =>
            workflowService.vincularTenantGrupo(grupoId, tid, justSox).catch(() => {})
          )
        );
      })
      .then(() => {
        setShowModal(false);
        fetchDados();
        showToast(
          editing
            ? 'Grupo econômico atualizado. Vínculos SOX registrados automaticamente.'
            : 'Grupo econômico cadastrado. Empresas vinculadas herdarão automaticamente as regras de alçada.'
        );
      })
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  const vincularEmpresa = (grupoId: number, tenantId: number) => {
    if (!tenantId) return;
    workflowService
      .vincularTenantGrupo(grupoId, tenantId, 'Vínculo realizado via interface de cadastro de grupos SOX.')
      .then(() => {
        fetchDados();
        showToast('Empresa vinculada ao grupo. Registro SOX gerado.');
      })
      .catch(() => {});
  };

  const toggleStatus = (g: GrupoEconomico) => {
    const novoStatus = g.status === 'ATIVO' ? 'INATIVO' : 'ATIVO';
    workflowService
      .atualizarGrupo(g.grupo_id, { nome: g.nome, status: novoStatus })
      .then(() => {
        fetchDados();
        showToast(`Grupo "${g.nome}" ${novoStatus === 'ATIVO' ? 'ativado' : 'desativado'}. Registro SOX gerado.`);
      })
      .catch(() => {});
  };

  return (
    <div className='wf-page'>
      <div className='wf-breadcrumb'>Home › Governança de TI › Cadastro de Grupos Econômicos</div>
      <div className='wf-header'>
        <div>
          <h1>Cadastro de Grupos Econômicos</h1>
          <p>Estruture e vincule empresas a grupos. Herança automática de alçadas e regras de SLA.</p>
        </div>
        <div className='wf-header__actions'>
          {fromOnboarding && (
            <button
              className='wf-btn wf-btn--secondary'
              onClick={() => history.push('/workflow-aprovacao/onboarding', { goToEtapa: onboardingEtapa })}
            >
              ← Onboarding Etapa {onboardingEtapa}
            </button>
          )}
          {!fromOnboarding && <button className='wf-btn wf-btn--secondary' onClick={() => history.goBack()}>Voltar</button>}
          <span className='wf-level7'>Acesso Nível 7 — TI</span>
          <button className='wf-btn wf-btn--primary' onClick={abrirNovo}>+ Novo grupo</button>
        </div>
      </div>

      {toast && <div className='wf-toast wf-toast--success'><strong>SOX</strong> {toast}</div>}

      {loading ? (
        <div className='wf-count' style={{ textAlign: 'center', padding: 40 }}>Carregando grupos...</div>
      ) : (
        <div className='wf-grupo-grid'>
          {grupos.length === 0 && (
            <div className='wf-count' style={{ textAlign: 'center', padding: 40 }}>
              Nenhum grupo cadastrado. Clique em "+ Novo grupo" para começar.
            </div>
          )}
          {grupos.map(g => {
            const empresasDoGrupo = empresas.filter(e => g.tenant_ids.includes(e.id));
            const isDetalhe = detalhe?.grupo_id === g.grupo_id;
            const empresasDisponiveis = empresas.filter(e => !g.tenant_ids.includes(e.id));

            return (
              <section key={g.grupo_id} className={`wf-card wf-grupo-card ${g.status === 'INATIVO' ? 'is-inativo' : ''}`}>
                <div className='wf-grupo-header'>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                      <h2 style={{ margin: 0, fontSize: 15 }}>{g.nome}</h2>
                      <span className={`wf-badge ${g.status === 'ATIVO' ? 'wf-badge--aprovada' : 'wf-badge--bloqueada'}`}>{g.status}</span>
                    </div>
                    <span style={{ fontSize: 11, color: '#7e8299' }}>
                      Responsável: {g.responsavel} &nbsp;|&nbsp; {empresasDoGrupo.length} empresa(s) vinculada(s)
                    </span>
                  </div>
                  <div className='wf-table-actions'>
                    <button className='wf-btn wf-btn--secondary wf-btn--sm' onClick={() => setDetalhe(isDetalhe ? null : g)}>
                      {isDetalhe ? 'Fechar' : 'Ver empresas'}
                    </button>
                    <button className='wf-btn wf-btn--secondary wf-btn--sm' onClick={() => abrirEditar(g)}>Editar</button>
                    <button
                      className={`wf-btn wf-btn--sm ${g.status === 'ATIVO' ? 'wf-btn--danger-outline' : 'wf-btn--secondary'}`}
                      onClick={() => toggleStatus(g)}
                    >
                      {g.status === 'ATIVO' ? 'Desativar' : 'Ativar'}
                    </button>
                  </div>
                </div>

                {isDetalhe && (
                  <div style={{ marginTop: 16, borderTop: '1px solid #edf1f5', paddingTop: 14 }}>
                    {empresasDisponiveis.length > 0 && (
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
                        <select
                          style={{ flex: 1, minHeight: 34, padding: '4px 8px', border: '1px solid #dfe3ea', borderRadius: 6, fontSize: 12 }}
                          defaultValue={0}
                          onChange={e => {
                            const tid = Number(e.target.value);
                            if (tid) vincularEmpresa(g.grupo_id, tid);
                            e.target.value = '0';
                          }}
                        >
                          <option value={0}>— Vincular empresa —</option>
                          {empresasDisponiveis.map(e => (
                            <option key={e.id} value={e.id}>{e.nome} ({formatarCnpj(e.cnpj)})</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className='wf-table-wrap'>
                      <table className='wf-table'>
                        <thead><tr><th>Empresa</th><th>CNPJ</th><th>Responsável</th><th>Status</th></tr></thead>
                        <tbody>
                          {empresasDoGrupo.length === 0
                            ? <tr><td colSpan={4} className='wf-count'>Nenhuma empresa vinculada.</td></tr>
                            : empresasDoGrupo.map(e => {
                                const resp = usuarios.find(u => u.id === e.responsavelId);
                                return (
                                  <tr key={e.id}>
                                    <td><strong>{e.nome}</strong></td>
                                    <td>{formatarCnpj(e.cnpj)}</td>
                                    <td>
                                      {resp
                                        ? (resp.nivel ? `${resp.nome} — N${resp.nivel}` : resp.nome)
                                        : <span className='wf-danger-text'>Sem responsável</span>}
                                    </td>
                                    <td>
                                      <span className={`wf-badge ${e.status === 'ATIVA' ? 'wf-badge--aprovada' : 'wf-badge--aguardando_aprovacao'}`}>
                                        {e.status}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className='wf-modal-bg'>
          <div className='wf-modal wf-modal--wide'>
            <div className='wf-modal__header'>
              <div>
                <h2>{editing ? 'Editar grupo econômico' : 'Novo grupo econômico'}</h2>
                <p>Campos obrigatórios marcados com *</p>
              </div>
              <button onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className='wf-modal__body'>
              <div className='wf-form-grid'>
                <label>
                  Nome do grupo *
                  <input
                    value={form.nome}
                    onChange={e => { const v = e.target.value; setForm(f => ({ ...f, nome: v })); }}
                    placeholder='Ex: Grupo Holding XYZ'
                  />
                </label>
                <label>
                  Status
                  <select
                    value={form.status}
                    onChange={e => { const v = e.target.value as GrupoEconomico['status']; setForm(f => ({ ...f, status: v })); }}
                  >
                    <option value='ATIVO'>Ativo</option>
                    <option value='INATIVO'>Inativo</option>
                  </select>
                </label>
                <label style={{ gridColumn: '1/-1' }}>
                  Justificativa SOX <span style={{ color: '#f64e60', fontSize: 10 }}>(obrigatória)</span>
                  <CampoJustificativaSox justificativa={justificativa} rows={3} placeholder='Motivo para fins de auditoria SOX...' />
                </label>
              </div>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 7, fontSize: 12, fontWeight: 600, color: '#5e6278', marginTop: 14 }}>
                Empresas vinculadas ao grupo
                <input
                  type='search'
                  value={buscaEmpresa}
                  onChange={e => { const v = e.target.value; setBuscaEmpresa(v); }}
                  placeholder='Buscar empresa por nome ou CNPJ...'
                  aria-label='Buscar empresa'
                />
                <div className='wf-multiselect'>
                  {empresasFiltradas.length === 0 && (
                    <p style={{ margin: 8, fontSize: 12, color: '#a1a5b7', fontWeight: 400 }}>Nenhuma empresa encontrada para a busca.</p>
                  )}
                  {empresasFiltradas.map(e => {
                    const grupoAtual = grupos.find(g => g.tenant_ids.includes(e.id) && g.grupo_id !== editing?.grupo_id);
                    return (
                      <label
                        key={e.id}
                        className={`wf-multiselect__item ${form.tenant_ids.includes(e.id) ? 'is-selected' : ''}`}
                        onClick={() => toggleEmpresa(e.id)}
                      >
                        <span className='wf-multiselect__check'>{form.tenant_ids.includes(e.id) ? '✓' : ''}</span>
                        <div>
                          <strong>{e.nome}</strong>
                          {grupoAtual
                            ? <span>{SEPARADOR_VINCULO}{descricaoVinculoGrupo(grupoAtual.nome)}</span>
                            : <span style={{ display: 'block' }}>{formatarCnpj(e.cnpj)}</span>}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </label>
              {form.tenant_ids.length > 0 && (
                <div className='wf-sod' style={{ marginTop: 14 }}>
                  <b>✓</b>
                  <div>
                    <strong>Herança automática de alçadas</strong>
                    <p>{form.tenant_ids.length} empresa(s) herdarão as regras de alçada e SLA configuradas para o Grupo Total.</p>
                  </div>
                </div>
              )}
            </div>
            <div className='wf-modal__footer'>
              <button className='wf-btn wf-btn--secondary' onClick={() => setShowModal(false)} disabled={saving}>Cancelar</button>
              <button
                className='wf-btn wf-btn--primary'
                disabled={!form.nome.trim() || !justificativa.texto.trim() || saving}
                onClick={salvar}
              >
                {saving ? 'Salvando...' : editing ? 'Salvar alterações' : 'Cadastrar grupo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CadastroGruposPage;
